import type { ReactNode } from 'react';
import styles from './tile.module.scss';

export type TileImageProps = {
  /** Image URL to display */
  imageUrl?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Overlay content (e.g., reserved banner, favourite button) */
  overlay?: ReactNode;
  /** Actions overlay (e.g., share, edit buttons) - shown on hover */
  actions?: ReactNode;
  /** Whether to show a play icon (for video content) */
  showPlayIcon?: boolean;
  /** Custom placeholder content */
  placeholder?: ReactNode;
  /** Additional class name */
  className?: string;
};

/**
 * TileImage - Image section of a tile with optional overlays.
 */
export function TileImage({
  imageUrl,
  alt = '',
  overlay,
  actions,
  showPlayIcon = false,
  placeholder,
  className
}: TileImageProps) {
  const hasImage = !!imageUrl;

  return (
    <div className={`${styles.tileImageContainer} ${className || ''}`}>
      {hasImage ? (
        <div
          className={styles.tileImage}
          style={{ backgroundImage: `url(${imageUrl})` }}
          role="img"
          aria-label={alt}
        />
      ) : (
        <div className={styles.tileImagePlaceholder} aria-hidden="true">
          {placeholder || (
            <svg
              viewBox="0 0 1200 1200"
              fill="currentColor"
              className={styles.placeholderIcon}
            >
              <path d="m360 1096.6c0.19922 0.19922 0.39844 0.19922 0.60156 0.39844 3.1992 2 6.8008 3 10.398 3h302.8c11 0 20-9 20-20v-3c0-50.602-35.199-93.199-82.602-104.6 19-29.801 41.602-77 30.801-122.2-21-89.801-103.8-112.2-144.6-111.6-11 0.19922-19.801 9.1992-19.602 20.199s9.1992 20 20.199 19.801c3.6016-0.19922 86.199 0.39844 105.2 80.801 10.199 43-28.398 99-44.199 116.8-5.1992 5.8008-6.3984 14.199-3.1992 21.398 3.1992 7.1992 10.398 11.801 18.199 11.801h12.398c31.398 0 57.801 21.398 65.199 50.398l-257.59 0.003906h-0.19922c-1.1992 0-8.1992-0.19922-18.801-1.3984-48-34.398-53.602-95.801-53.801-97.602-17.398-122.8 69.602-209.8 146.4-286.6 37.602-37.602 73.398-73.199 95-111 10.398-18.199 17.801-40 22.398-66 19.801 7.1992 41.602 11.199 64.801 11.199h6.3984c39.398-1.1992 60.398-20.398 71-36.398 45.398-68-4.8008-206.6-10.801-222.2-4-10.398-15.398-15.602-25.801-11.602-10.398 4-15.602 15.398-11.602 25.801 14 37 44 142.4 15 185.8-8.1992 12.199-20.602 18-39 18.602-54.398 1.6016-100-22.199-119.2-62.398-25.602-54-1-130.6 67.602-210.2 0.19922-0.19922 0.19922-0.39844 0.19922-0.60156l0.19922-0.19922c17.801-22.602 48.602-39 99.602-52.801 64.602-17.398 170.6 62.398 201.4 87.398 14.602 11.801 32.199 18 51 18h17.199c22.602 0 41 18.398 41 41l0.007812 11.398c0 40.199-21 78-54.602 98.801-37.398 22.801-81.199 34.199-130.6 34.199-11 0-20 9-20 20v67.199c0 3.8008 1.1992 7.6016 3.1992 11 0.60156 0.80078 57.801 90.398 33.398 184.8-4.3984 16.602-9.3984 33.602-14.398 50.602-14.398 48.602-29.199 99-29.199 142 0 45.801 17.398 78.801 51.398 98.199 3 1.8008 6.3984 2.6016 9.8008 2.6016 31.801 0 58.602 22 65.801 50.398h-92.797c-13.801 0-26.602-8.8008-31.602-21.602l-47-121.4v-145.4c0-11-9-20-20-20s-20 9-20 20v149.2c0 2.3984 0.39844 5 1.3984 7.1992l48.398 124.8c11.004 28.398 38.605 47.398 68.805 47.398h115c10.398 0 19.199-8 20-18.398l0.19922-4.6016c0-57.199-45.199-104.2-101.8-107.2-18.801-12.398-27.199-31.398-27.199-61 0-37.199 14-84.801 27.602-130.6 5.1992-17.398 10.199-34.801 14.801-51.801 25.602-98.801-22-188.8-35.398-211.6v-42.199c48.801-2.8008 92.801-16 131.4-39.602 45.398-28 73.801-79 73.801-133v-11.199c0-44.602-36.398-81-81-81l-17.402 0.003906c-9.6016 0-18.398-3.1992-25.801-9.1992-55.199-44.398-159.2-115.8-237-94.801-59.801 16.199-97 36.801-120.6 66.598v0.19922c-108 125.8-92 212.2-72.801 252.6 10.602 22.199 27.199 40.602 47.398 54.801-3.3984 27-9.8008 48.801-19.199 65.398-19.199 33-53 66.801-88.801 102.6-79.199 79.199-177.8 177.8-158 319 0.19922 2.1992 3.1992 37.602 24.199 74.199-35.199-15.602-73.398-43.199-98.199-92-56.801-111 16.398-214 19.801-218.6 6.6016-8.8008 4.6016-21.398-4.1992-28-9-6.6016-21.398-4.6016-28 4.1992-3.8008 5.1992-91.602 127-23.199 260.6 49 96 139.4 123.4 188.4 131.2z" />
              <path d="m792.4 270.2c2.3984-1 4.6016-2.6016 6.3984-4.3984 3.8008-3.8008 5.8008-8.8008 5.8008-14.199 0-5.1992-2-10.398-5.8008-14-1.8008-2-4-3.3984-6.3984-4.3984-7.3984-3-16.398-1.1992-21.801 4.3984-3.8008 3.6016-6 8.8008-6 14 0 5.3984 2.1992 10.398 6 14.199 3.6016 3.8008 8.8008 5.8008 14 5.8008 2.8008 0 5.2031-0.40234 7.8008-1.4023z" />
            </svg>
          )}
        </div>
      )}

      {showPlayIcon && (
        <span className={styles.playIcon} aria-label="Play video">
          <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      )}

      {overlay && <div className={styles.tileImageOverlay}>{overlay}</div>}

      {actions && <div className={styles.tileImageActions}>{actions}</div>}
    </div>
  );
}
